'use client';

import Image from 'next/image';
import { HiOutlineArrowCircleRight } from 'react-icons/hi';
import ModuleOverviewSidebar, {
  SidebarItem,
} from '@/components/ModuleOverview/ModuleOverviewSidebar';
import { selectedModuleOverview } from '@/libs/atoms';
import { useAtom } from 'jotai/index';
import { useEffect, useState } from 'react';
import { PortableTextBlock } from '@sanity/types';
import type { ArbitraryTypedObject } from '@portabletext/types';
import { PortableText } from '@portabletext/react';
import { getSanityImageUrl } from '@/libs/sanity/client';
import Link from 'next/link';
import { BlurInH2 } from '@/components/eldora-ui/blur-in-h2';
import {
  Accordion,
  AccordionContainer,
  AccordionItem,
  AccordionWrapper,
  AccordionHeader,
  AccordionPanel,
} from '@/components/ui/accordion';
import { TABLET_SCREEN_SIZE } from '@/utils/constants';
import { useMediaQuery } from 'usehooks-ts';
import { PortableTextWrapper } from '../PortableTextWrapper';

export type ModuleOverviewItem = {
  title?: string;
  subTitle?: string;
  firstContent?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  secondContent?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  fullSecondContent?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  link?: any;
  _key?: string;
};

export type ModuleOverviewProp = {
  name?: string;
  title?: string;
  description?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  image?: any;
  link?: any;
  _key?: string;
  items?: ModuleOverviewItem[];
};

type ModuleOverviewSectionProps = {
  name: string;
  modules: ModuleOverviewProp[];
};

const ModuleOverview = ({ modules }: ModuleOverviewSectionProps) => {
  const sidebarItems = modules.map<SidebarItem>((module, index) => ({
    order: index,
    id: module._key,
    type: module.name,
    content: module.name,
    description: module.description,
    href: module.link,
    isActive: index === 0,
  }));

  const [selectedModuleOverviewState] = useAtom(selectedModuleOverview);
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const [selectedModuleItems, setSelectedModuleItems] = useState(modules[0]);

  useEffect(() => {
    if (modules) {
      const selectedItem = modules.find(
        (module) => module?.name === selectedModuleOverviewState
      );
      setSelectedModuleItems(selectedItem);
    }
  }, [modules, selectedModuleOverviewState]);

  return isTablet ? (
    <AccordionContainer className="grid grid-cols-1 col-span-4">
      <h2 className="text-2xl mb-3">
        Explore Simplicity<sup>®</sup>{' '}
        <span className="text-primary">Sectors</span>
      </h2>
      <AccordionWrapper>
        <Accordion>
          {modules.map((module) => (
            <AccordionItem key={module._key} value={module._key.toString()}>
              <AccordionHeader leftIcon={true}>
                <div className="cursor-pointer">
                  Simplicity<sup>®</sup>{' '}
                  <span className="text-primary">{module.name}</span>
                </div>
              </AccordionHeader>
              <AccordionPanel>
                <PortableTextWrapper value={module.description} />
                {module.link && module.link?.internal && (
                  <Link
                    href={module.link?.internal.ref?.metadata?.slug?.current}
                    className="mb-4 text-sm font-medium border-2 border-primary text-primary px-3 py-2 hover:bg-gray-200 w-[60%] flex flex-row justify-between align-baseline"
                  >
                    Explore
                    <HiOutlineArrowCircleRight
                      className="text-primary text-lg"
                      size="18"
                    />
                  </Link>
                )}
                <AccordionContainer className="grid grid-cols-1">
                  <AccordionWrapper>
                    <Accordion>
                      {module.items?.map((item) => (
                        <AccordionItem
                          key={item._key}
                          value={item._key.toString()}
                        >
                          <AccordionHeader leftIcon={true}>
                            <div className="cursor-pointer">{item.title}</div>
                          </AccordionHeader>
                          <AccordionPanel>
                            <div className="text-sm [&>ul]:list-disc [&>ul>li]:pb-2 [&>ul>li]:ml-4 pl-3">
                              <PortableText value={item.fullSecondContent} />
                            </div>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionWrapper>
                </AccordionContainer>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </AccordionWrapper>
    </AccordionContainer>
  ) : (
    <>
      <div id="sidebar" className="col-span-4 md:col-span-1 md:max-w-[90%]">
        <ModuleOverviewSidebar sidebarItems={sidebarItems} />
      </div>
      <div
        className="col-span-4 md:col-span-3 scroll-mt-[130px]"
        id="simplicity-sectors"
      >
        <div className="py-4 md:py-8 grid grid-cols-1 lg:grid-cols-3 gap-y-6 gap-x-0 lg:gap-x-6">
          <div className="col-span-1 flex-col flex gap-y-2 gap-x-1 justify-between">
            <div className="mb-6 flex flex-col gap-x-4">
              <BlurInH2 className="font-semibold">
                Simplicity<sup>®</sup>
                <br />{' '}
                <span className="font-light text-primary">
                  {selectedModuleItems?.title}
                </span>
              </BlurInH2>
              <div>
                {selectedModuleItems?.description && (
                  <PortableText value={selectedModuleItems?.description} />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-x-4">
              <Image
                src={getSanityImageUrl(selectedModuleItems?.image)}
                alt={selectedModuleItems?.title}
                className="mb-8"
                loading="lazy"
                width="500"
                height="500"
              />
              {selectedModuleItems.link &&
                selectedModuleItems.link.internal && (
                  <Link
                    href={
                      selectedModuleItems.link.internal.ref?.metadata?.slug
                        ?.current
                    }
                    className="border border-primary text-primary p-3 hover:bg-gray-200 w-full flex flex-row justify-between align-baseline !px-3"
                  >
                    Explore
                    <HiOutlineArrowCircleRight
                      className="text-primary text-lg"
                      size="22"
                    />
                  </Link>
                )}
            </div>
          </div>
          <div className="mt-2 md:mt-0 col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-y-0 content-start">
            {selectedModuleItems.items?.map((item) => {
              return (
                <Link
                  href={
                    item.link && item.link.internal
                      ? item.link.internal.ref?.metadata?.slug?.current
                      : '#'
                  }
                  key={item._key}
                  className="overflow-hidden group perspective border h-[200px] lg:h-[222px] relative"
                >
                  <div className="transition duration-700 lg:group-hover:translate-y-[-222px] absolute flex flex-col justify-between h-full p-3">
                    <div className="normal-content">
                      <h3 className="text-md pb-2">{item.title}</h3>
                      {item.subTitle && (
                        <h4 className="text-sm pb-1">{item.subTitle}</h4>
                      )}
                      <div className="text-xs">
                        {item?.firstContent && (
                          <PortableText value={item?.firstContent} />
                        )}
                      </div>
                    </div>
                    <div className="text-primary hover:text-blue-700">
                      <HiOutlineArrowCircleRight
                        className="text-primary text-lg"
                        size="22"
                      />
                    </div>
                  </div>
                  <div className="absolute p-3 hover-content bg-gray-100 transition duration-700 group-hover:translate-y-[0px] translate-y-[222px] flex flex-col justify-between h-full w-full">
                    <div className="normal-content">
                      <h3 className="text-md pb-1">{item.title}</h3>
                      {item.subTitle && (
                        <h4 className="text-sm pb-1">{item.subTitle}</h4>
                      )}
                      <div className="text-xs [&>ul]:list-disc [&>ul>li]:pb-1 [&>ul>li]:ml-4">
                        {item?.secondContent && (
                          <PortableText value={item?.secondContent} />
                        )}
                      </div>
                    </div>
                    <div className="text-primary hover:text-blue-700">
                      <HiOutlineArrowCircleRight
                        className="text-primary text-lg"
                        size="22"
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModuleOverview;
